/**
 * TRX (Transactional) experiment — parent ticket TM-1296.
 *
 * Lands a paused customer directly on /menu for the earliest available
 * delivery date and slot, bypassing the /delivery-details step. Consumed
 * by TRX-experiment surfaces: the Gousto Anytime modal on MyGousto, and
 * (planned) the Order Confirmation page.
 *
 * Remove or fold into the standard flow once the TRX experiment ships.
 */
import type { RootStateOrAny } from 'react-redux'
import type { AnyAction } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'

import { basketDateChange, basketSlotChange, getMenuCutoffUntil } from 'actions/basket'
import { boxSummaryDeliveryDaysLoad } from 'actions/boxSummary'
import { redirect } from 'actions/redirect'
import userActions from 'actions/user'
import { client as clientRoutes } from 'config/routes'
import { getLandingDay } from 'utils/deliveries'
import logger from 'utils/logger'

type GetState = () => RootStateOrAny

export const startOneOffOrder =
  () =>
  async (
    dispatch: ThunkDispatch<RootStateOrAny, unknown, AnyAction>,
    getState: GetState,
  ): Promise<void> => {
    try {
      // Mirrors the data fetching 'delivery-details' runs on mount.
      await Promise.all([
        dispatch(userActions.userLoadData()),
        dispatch(userActions.userFetchShippingAddresses()),
        dispatch(userActions.userLoadOrders(true)),
      ])

      // 'transactional' is the order type that exposes one-off slots to unsubscribed customers.
      const cutoffUntil = await getMenuCutoffUntil()
      await dispatch(boxSummaryDeliveryDaysLoad(undefined, cutoffUntil, 'transactional'))

      // Delegate day/slot pre-selection to the same helper delivery-details uses, so
      // the two surfaces stay in sync. cantLandOnOrderDate keeps the one-off flow off
      // days where the customer already has an order.
      const { date, slotId } = getLandingDay(getState(), {
        useCurrentSlot: true,
        useBasketDate: false,
        cantLandOnOrderDate: true,
      })

      if (!date || !slotId) {
        dispatch(redirect(clientRoutes.deliveryDetails))

        return
      }

      dispatch(basketDateChange(date))
      dispatch(basketSlotChange(slotId))
      dispatch(redirect(clientRoutes.menu))
    } catch (err: any) {
      logger.error(err)
      dispatch(redirect(clientRoutes.deliveryDetails))
    }
  }
